import { useState, useEffect, useCallback } from 'react';
import useSalla from './useSalla';
import { log, error, toggleDarkMode, inIframe, parentMessage } from './utils';

function useSetupApp(initialData: any) {
    const [isReady, setIsReady] = useState(false);
    const [supportWebAuth, setSupportWebAuth] = useState(false);
    const [withoutReload, setWithoutReload] = useState(false);
    const [hideHeader, setHideHeader] = useState(false);
    const [outlineButtonNoBorder, setOutlineButtonNoBorder] = useState(false);
    const [data, setData] = useState(() => initialData);
    const salla = useSalla();
    const startTheAppCallback = useCallback(() => {
        if (!data) {
            return;
        }
        setSupportWebAuth(data.supportWebAuth);
        setWithoutReload(data.withoutReload);
        setHideHeader(data.hideHeader || false);
        setOutlineButtonNoBorder(data.outlineButtonNoBorder || false);
        toggleDarkMode(data.isDark);

        document.documentElement.dir = data.lang === 'ar' ? 'rtl' : 'ltr';
        document.body.dir = data.lang === 'ar' ? 'rtl' : 'ltr';
        document.body.classList.add(data.lang === 'ar' ? 'rtl' : 'ltr');
        // data.headers && salla.api.setHeaders(data.headers);
        //@ts-ignore
        Object.entries(data.storage||{}).forEach(([key, value]) => localStorage.setItem(key, value));
        if(data.storage?.token){
            salla.cookie.set('s-token', data.storage.token);
        }


        const headers = salla.api.getHeaders();

        if (data.settingsOverrides?.headers) {
            Object.assign(headers, data.settingsOverrides.headers);
        }

        Promise.resolve()
            .then(() => {
                const hasSettings = Object.keys(data.settings || {}).length > 0;
                const hasWebviewLogin = data.settings?.store?.features?.includes('app-webview-login');

                if (hasSettings && !hasWebviewLogin) {
                    console.log('Twilight config passed from parent 🤩');
                    return data.settings;
                }
                console.log('⚡️ Getting Twilight config via api request...');

                return fetch(`${data.settings?.store?.apiUrl ?? data.apiUrl ?? 'https://api.salla.dev/store/v1'}/store/settings`, {
                    headers: {
                        "accept-language": data.lang || 'ar',
                        ...headers,
                        "Store-Identifier": data.id,
                    },
                }).then(res => res.json()).then(res => res.data);
            })
            .then((config) => {
                if (data.settingsOverrides) {
                    const deepMerge = (target: any, source: any): any => {
                        for (const key of Object.keys(source)) {
                            if (
                                source[key] &&
                                typeof source[key] === 'object' &&
                                !Array.isArray(source[key]) &&
                                target[key] &&
                                typeof target[key] === 'object' &&
                                !Array.isArray(target[key])
                            ) {
                                deepMerge(target[key], source[key]);
                            } else {
                                target[key] = source[key];
                            }
                        }
                        return target;
                    };
                    deepMerge(config, data.settingsOverrides);
                }

                const hasWebviewLogin = data.settings?.store?.features?.includes('app-webview-login');
                if (hasWebviewLogin && !config.store?.features?.includes('app-webview-login')) {
                    config.store = config.store || {};
                    config.store.features = config.store.features || [];
                    config.store.features.push('app-webview-login');
                }

                if (hasWebviewLogin && data.settings?.jitsu) {
                    config.jitsu = data.settings.jitsu;
                }

                if (hasWebviewLogin && data.settings?.login?.social) {
                    config.login = config.login || {};
                    config.login.social = data.settings.login.social;
                } else if (hasWebviewLogin) {
                    config.login = config.login || {};
                    config.login.social = Object.fromEntries(
                        Object.keys(config.login.social || {}).map((key) => [key, false])
                    );
                }

                let user = config.user;
                let isUserIdValid = false;
                Salla.api.setHeader("s-origin", data.origin);

                Salla.api.setHeader("s-source", 'account');
                Object.keys(config.headers || {}).forEach((key) => {
                  if (key.toLowerCase() === "s-origin") {
                    delete config.headers[key];
                  }
                });

                if (user) {
                    isUserIdValid = /^\d+$/.test(user.id);
                }

                if (!isUserIdValid) {
                    const storedUser = salla.storage.get('user');
                    if (storedUser) {
                        user = storedUser;
                    }
                }

                // Set the theme mode to live to skip rendering theme preview alert
                if(config.theme.mode){
                    config.theme.mode = 'live';
                }
                // Set the maintenance to null to skip rendering maintenance alert
                config.maintenance = null;
                config.user = user;
                salla.init(config);
            })
            .then(() => salla.onReady())
            .then(() => salla.lang.onLoaded())
            .then(() => salla.config.get('theme.color') as { [key: string]: string } || {})
            .then(colors => Object.entries(colors).forEach(([key, value]) => document.documentElement.style.setProperty(`--color-${key.replace(/_/g, '-')}`, value)))
            .then(() => log('Salla Twilight initialized successfully', salla))
            .then(() => setIsReady(data.isGuest || salla.config.isGuest() || window.location.pathname.includes('logout')))
            .then(() => Salla.analytics.track('Authentication Initiated', {
              source: inIframe() ? 'iframe' : 'modal'
            }))
            .catch(er => {
                error('Error initializing Salla Twilight', er);
                parentMessage({ error: er?.message || 'Failed to load settings' }, 'error');
            });
    }, [data, salla]);
    useEffect(() => startTheAppCallback(), [startTheAppCallback]);
    const startTheApp = (data: any) => setData(data);


    return { salla, isReady, supportWebAuth, withoutReload, hideHeader, outlineButtonNoBorder, startTheApp };
}

export default useSetupApp;
